import { REVIEW_TEST_IDS } from '@/components/admin/sessions/contracts';
import { useReviewApi } from '@/components/admin/sessions/review/review-context';
import { TooltipShield } from '@/components/admin/tooltip-shield';
import { DISABLED_INERT } from '@/lib/disabled-inert';
import { type DoubtState, lockSentence } from '@/lib/review-gestures';
import { caseNumber } from '@/lib/review-transcript';
import type { ReviewExtract } from '@matter_extractor/shared';
import { Icon, Tooltip } from '@pms-digital/design-system';

/**
 * The « ? » and the cross of an extract - ONE rendering, in its case and in the
 * popover of a passage placed (relecture.md §5.3, D24): « le même "?" et la
 * même croix, alignés en colonne », with the same hover - a ground and a
 * border, amber for the « ? », red for the cross.
 *
 * ## The locked « ? »
 *
 * Under « un passage, un seul objectif », a passage in two cases carries its
 * « ? » on both placements, and neither can lift it (§5.4): the button is truly
 * `disabled` - under `TooltipShield`, so its bubble still opens - and stays
 * amber, because a locked doubt is a doubt that IS there. The only way out is
 * the cross of one of the two.
 */

const SQUARE =
  'inline-flex size-[22px] shrink-0 cursor-pointer items-center justify-center rounded-md border font-bold text-caption leading-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-not-allowed';

export interface ReviewExtractControlsProps {
  readonly extract: ReviewExtract;
  readonly doubt: DoubtState;
}

export function ReviewExtractControls({ extract, doubt }: ReviewExtractControlsProps) {
  const { apply, canWrite, busy, order } = useReviewApi();
  const number = caseNumber(order, extract.objectiveId);
  const doubtOff = !canWrite || doubt.locked;
  // A locked « ? » that is up keeps its amber: the recipe of an inert control
  // would grey the one thing the button says.
  const doubtTone = extract.doubt
    ? 'border-jaune-deep bg-jaune-tint text-jaune-deep'
    : 'border-gray-200 bg-white text-gray-500';
  const doubtClass = [
    SQUARE,
    doubtTone,
    doubtOff
      ? extract.doubt
        ? ''
        : DISABLED_INERT
      : 'hover:border-jaune-deep hover:bg-jaune-tint hover:text-jaune-deep',
  ].join(' ');
  const doubtLabel = doubt.locked
    ? 'Doute verrouillé : retirez l’un des deux placements pour le lever'
    : extract.doubt
      ? 'Lever le doute sur ce placement'
      : 'Poser un doute sur ce placement';

  return (
    <span className="inline-flex shrink-0 items-center gap-1">
      <Tooltip content={doubtLabel} side="top">
        <TooltipShield closed={doubtOff}>
          <button
            type="button"
            data-testid={REVIEW_TEST_IDS.doubt}
            aria-pressed={extract.doubt}
            aria-label={doubtLabel}
            disabled={doubtOff || busy}
            className={doubtClass}
            onClick={() =>
              void apply([{ op: 'set-doubt', extractId: extract.id, doubt: !extract.doubt }])
            }
          >
            ?
          </button>
        </TooltipShield>
      </Tooltip>
      {canWrite ? (
        <Tooltip content={`Retirer de la case ${number}`} side="top">
          <button
            type="button"
            data-testid={REVIEW_TEST_IDS.unrange}
            aria-label={`Retirer de la case ${number}`}
            disabled={busy}
            className={`${SQUARE} border-gray-200 bg-white text-gray-500 hover:border-rouge-deep hover:bg-rouge-tint hover:text-rouge-deep ${DISABLED_INERT}`}
            onClick={() => void apply([{ op: 'unrange', extractId: extract.id }])}
          >
            <Icon name="close" size={14} />
          </button>
        </Tooltip>
      ) : null}
    </span>
  );
}

/** The sentence under a locked « ? » (§5.4), or null. */
export function ReviewDoubtLock({ doubt }: { readonly doubt: DoubtState }) {
  if (!doubt.locked) return null;
  return (
    <span data-testid={REVIEW_TEST_IDS.lock} className="text-jaune-deep text-caption">
      {lockSentence(doubt.alsoIn)}
    </span>
  );
}
